/*
 * Custom Slider — reusable, dependency-free horizontal slider.
 * Markup:
 *   <div data-slider>
 *     <div data-slider-track> ...slides... </div>
 *     <button data-slider-prev></button>
 *     <button data-slider-next></button>
 *     <div data-slider-dots></div>  (optional)
 *   </div>
 * Slide width is controlled entirely by CSS (flex-basis + media queries),
 * so it's fully responsive. JS only handles paging, drag, and button state.
 */
(function () {
  'use strict';

  function CustomSlider(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    if (!this.track) return;

    this.onScroll = this.update.bind(this);
    this.bind();
    this.update();
  }

  CustomSlider.prototype.step = function () {
    var slide = this.track.children[0];
    if (!slide) return this.track.clientWidth;
    var style = getComputedStyle(this.track);
    var gap = parseFloat(style.columnGap || style.gap || 0) || 0;
    var slideWidth = slide.getBoundingClientRect().width + gap;
    var perView = Math.max(1, Math.round(this.track.clientWidth / slideWidth));
    return slideWidth * perView;
  };

  CustomSlider.prototype.scrollByStep = function (dir) {
    this.track.scrollBy({ left: dir * this.step(), behavior: 'smooth' });
  };

  CustomSlider.prototype.update = function () {
    var t = this.track;
    var atStart = t.scrollLeft <= 1;
    var atEnd = t.scrollLeft + t.clientWidth >= t.scrollWidth - 1;
    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.setAttribute('aria-disabled', atStart);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.setAttribute('aria-disabled', atEnd);
    }
  };

  CustomSlider.prototype.bind = function () {
    var self = this;
    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.scrollByStep(-1); });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.scrollByStep(1); });
    this.track.addEventListener('scroll', function () {
      window.requestAnimationFrame(self.onScroll);
    }, { passive: true });
    window.addEventListener('resize', self.onScroll);

    // pointer drag (desktop) — NO pointer capture (capture retargets the
    // click to the track and breaks card links). Track via window listeners
    // and only treat it as a drag once the pointer moves past a threshold.
    var down = false, startX = 0, startScroll = 0, moved = false;
    this.track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return; // let native touch handle it
      down = true; moved = false;
      startX = e.clientX;
      startScroll = self.track.scrollLeft;
    });
    window.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 5) { moved = true; self.track.classList.add('is-dragging'); }
      if (moved) { self.track.scrollLeft = startScroll - dx; e.preventDefault(); }
    });
    function endDrag() {
      down = false;
      self.track.classList.remove('is-dragging');
    }
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    // Only swallow the click when an actual drag happened (so taps navigate).
    this.track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
      moved = false;
    }, true);
  };

  function boot(scope) {
    (scope || document).querySelectorAll('[data-slider]').forEach(function (el) {
      if (el.dataset.sliderInit === 'true') return;
      el.dataset.sliderInit = 'true';
      new CustomSlider(el);
    });
  }

  window.CustomSlider = CustomSlider;

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', function () { boot(); });
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
